import { FirebaseApp, getApp, getApps, initializeApp } from 'firebase/app'
import {
  Analytics,
  getAnalytics,
  isSupported,
  logEvent as firebaseLogEvent,
  setUserId as firebaseSetUserId,
  setCurrentScreen as firebaseSetCurrentScreen
} from 'firebase/analytics'

let app: FirebaseApp | null = null
let analyticsPromise: Promise<Analytics | null> | null = null

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY ?? process.env.EXPO_PUBLIC_FIREBASE_API_KEY,
  appId: process.env.REACT_APP_FIREBASE_APP_ID ?? process.env.EXPO_PUBLIC_FIREBASE_APP_ID,
  measurementId:
    process.env.REACT_APP_FIREBASE_MEASUREMENT_ID ?? process.env.EXPO_PUBLIC_FIREBASE_MEASUREMENT_ID,
  projectId: 'verzekeringapp'
}

const hasRequiredConfig = Boolean(firebaseConfig.apiKey && firebaseConfig.appId)

export const getFirebaseApp = (): FirebaseApp | null => {
  if (!hasRequiredConfig) {
    console.log('Firebase configuration is missing. Firebase will not be initialized.')

    return null
  }

  if (app) {
    return app
  }

  app = getApps().length ? getApp() : initializeApp(firebaseConfig)

  return app
}

export const getFirebaseAnalytics = async (): Promise<Analytics | null> => {
  if (analyticsPromise) {
    return analyticsPromise
  }

  analyticsPromise = (async () => {
    const firebaseApp = getFirebaseApp()

    if (!firebaseApp) {
      return null
    }

    if (typeof window === 'undefined') {
      return null
    }

    const supported = await isSupported()

    if (!supported) {
      return null
    }

    return getAnalytics(firebaseApp)
  })()

  return analyticsPromise
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const logEvent = async (name: string, params?: Record<string, any>) => {
  const analytics = await getFirebaseAnalytics()

  if (!analytics) {
    console.log(`Analytics not supported. Event "${name}" will not be logged.`)
    console.log('Event parameters were:', params)

    return
  }

  console.log('Logging event', name, params)
  firebaseLogEvent(analytics, name, {
    ...params,
    debug_mode: process.env.NODE_ENV === 'development'
  })
}

export const setCurrentScreen = async (screenName: string) => {
  const analytics = await getFirebaseAnalytics()

  if (!analytics) {
    return
  }

  firebaseSetCurrentScreen(analytics, screenName)
}

export const setUserId = async (userId: string) => {
  const analytics = await getFirebaseAnalytics()

  if (!analytics) {
    return
  }

  firebaseSetUserId(analytics, userId)
}
